import { useState } from "react";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DndContext, PointerSensor, TouchSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, horizontalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const DEFAULT_CLS = "rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-medium text-muted-foreground cursor-grab active:cursor-grabbing transition-colors data-[state=active]:border-fuchsia-400/50 data-[state=active]:bg-fuchsia-500/15 data-[state=active]:text-fuchsia-300";

function SortablePill({ id, label, testid, className }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1, zIndex: isDragging ? 50 : undefined };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="touch-none">
      <TabsTrigger value={id} data-testid={testid} className={className}>{label}</TabsTrigger>
    </div>
  );
}

// Barre de sous-onglets réordonnables par glisser-déposer, avec mémorisation locale de l'ordre.
// items: [[value, label], ...]
export function DraggableSubTabs({ storageKey, items, testidPrefix = "subtab", triggerClassName }) {
  const defaultOrder = items.map((i) => i[0]);
  const labelMap = Object.fromEntries(items);
  const [order, setOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || "null");
      if (Array.isArray(saved)) {
        const valid = saved.filter((v) => defaultOrder.includes(v));
        const missing = defaultOrder.filter((v) => !valid.includes(v));
        return [...valid, ...missing];
      }
    } catch (e) { /* ignore */ }
    return defaultOrder;
  });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
  );
  const onDragEnd = ({ active, over }) => {
    if (over && active.id !== over.id) {
      const next = arrayMove(order, order.indexOf(active.id), order.indexOf(over.id));
      setOrder(next);
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch (e) { /* ignore */ }
    }
  };
  const cls = triggerClassName || DEFAULT_CLS;
  return (
    <TabsList className="flex h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={order} strategy={horizontalListSortingStrategy}>
          {order.map((v) => (
            <SortablePill key={v} id={v} label={labelMap[v]} testid={`${testidPrefix}-${v}`} className={cls} />
          ))}
        </SortableContext>
      </DndContext>
    </TabsList>
  );
}
